import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
  Transition,
} from '@headlessui/react';
import { CheckIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { Checkbox } from '@/components/chariot-ui';
import DOMPurify from 'dompurify';
import { Minus } from 'lucide-react';
import { Fragment, useMemo } from 'react';
import { toast } from 'sonner';
import { twMerge } from 'tailwind-merge';

import { useCustomerDisplayName } from '@/sections/customerManagement/hooks/useCustomerDisplayName';
import { useQueryBuilder } from '@/sections/insights/queryBuilder/state/useQueryBuilder';

// Security constants
const MAX_CUSTOMERS = 100;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * UserSelectorDropdown - Simple multi-select dropdown for choosing users in global query mode
 *
 * Displays when isGlobal={true}, allowing admins to scope queries to specific customer accounts.
 * Uses Headless UI Listbox for immediate state updates without an Apply button.
 *
 * Initialization happens in QueryUIProvider (centralized location).
 *
 * Security Features:
 * - XSS Protection: Sanitizes display names with DOMPurify (strips all HTML tags)
 * - Input Validation: Validates email format before selection
 * - DoS Protection: Enforces 100 customer selection limit
 */
export const UserSelectorDropdown = () => {
  const { usernames, setUsernames } = useQueryBuilder();
  const { isLoading } = useCustomerDisplayName();

  // Get all user emails sorted alphabetically by display name (or email as fallback)
  const allUserEmails = useMemo(
    () =>
      [...usernames]
        .sort((a, b) => {
          const aName = a.displayName || a.email;
          const bName = b.displayName || b.email;
          return aName.localeCompare(bName, undefined, { sensitivity: 'base' });
        })
        .map(u => u.email),
    [usernames]
  );

  // Check if all users are selected
  const allSelected = useMemo(
    () => usernames.length > 0 && usernames.every(u => u.isSelected),
    [usernames]
  );

  // Check if some (but not all) users are selected
  const someSelected = useMemo(
    () => usernames.some(u => u.isSelected) && !allSelected,
    [usernames, allSelected]
  );

  // Calculate display text for trigger button
  const displayText = useMemo(() => {
    if (isLoading) return 'Loading users...';

    const selectedUsernames = usernames.filter(u => u.isSelected);

    if (selectedUsernames.length === 0) return 'Select Users';
    if (allSelected) return 'All Users';

    if (selectedUsernames.length === 1) {
      const displayName = selectedUsernames[0].displayName;
      return DOMPurify.sanitize(displayName, { ALLOWED_TAGS: [] });
    }

    const firstDisplayName = selectedUsernames[0].displayName;
    const remaining = selectedUsernames.length - 1;
    return `${DOMPurify.sanitize(firstDisplayName, { ALLOWED_TAGS: [] })} +${remaining}`;
  }, [usernames, isLoading, allSelected]);

  // Handle Select All / Deselect All
  const handleSelectAllToggle = () => {
    if (allSelected || someSelected) {
      // Deselect all
      setUsernames(usernames.map(u => ({ ...u, isSelected: false })));
    } else {
      // Select all
      if (usernames.length > MAX_CUSTOMERS) {
        toast.error(`Maximum ${MAX_CUSTOMERS} customers can be selected`);
        return;
      }
      setUsernames(usernames.map(u => ({ ...u, isSelected: true })));
    }
  };

  // Handle individual user selection
  const handleUserToggle = (email: string) => {
    // SECURITY: Validate email format
    if (!EMAIL_REGEX.test(email)) {
      toast.error('Invalid email format');
      console.error('[UserSelectorDropdown] Invalid email format:', email);
      return;
    }

    const selectedCount = usernames.filter(u => u.isSelected).length;
    const targetUser = usernames.find(u => u.email === email);

    if (targetUser?.isSelected) {
      // Remove user - toggle isSelected to false
      setUsernames(
        usernames.map(u =>
          u.email === email ? { ...u, isSelected: false } : u
        )
      );
    } else {
      // Add user - toggle isSelected to true
      if (selectedCount >= MAX_CUSTOMERS) {
        toast.error(`Maximum ${MAX_CUSTOMERS} customers can be selected`);
        return;
      }
      setUsernames(
        usernames.map(u => (u.email === email ? { ...u, isSelected: true } : u))
      );
    }
  };

  return (
    <Listbox value={[]} onChange={() => {}} multiple>
      {({ open }) => (
        <div className="relative">
          <ListboxButton
            className={twMerge(
              'border-chariot-stroke bg-chariot-background-primary text-chariot-text-primary hover:bg-chariot-background-secondary',
              'flex items-center gap-2 rounded-md border px-2 py-1 text-xs transition-colors',
              open &&
                'border-chariot-brand-primary bg-chariot-background-secondary'
            )}
          >
            <span className="truncate">{displayText}</span>
            <ChevronDownIcon className="text-chariot-text-primary size-4 stroke-[1.5px]" />
          </ListboxButton>

          <Transition
            as={Fragment}
            leave="transition ease-in duration-100"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <ListboxOptions
              className={twMerge(
                'border-chariot-stroke bg-chariot-background-primary absolute right-0 z-50 mt-2 max-h-96 w-80 overflow-auto rounded-md border shadow-lg',
                'focus:outline-none'
              )}
            >
              {/* Select All Option */}
              <div
                className="border-chariot-stroke hover:bg-chariot-background-secondary cursor-pointer border-b-2 px-3 py-2"
                onClick={handleSelectAllToggle}
              >
                <div className="flex items-center gap-3">
                  {/* Tri-state checkbox */}
                  <div className="flex size-6 shrink-0 items-center justify-center">
                    {someSelected ? (
                      <div className="bg-chariot-brand-primary border-chariot-brand-primary flex size-4 items-center justify-center rounded-[2px] border-2">
                        <Minus className="size-3 stroke-[3px] text-white" />
                      </div>
                    ) : (
                      <Checkbox isChecked={allSelected} />
                    )}
                  </div>
                  <span className="text-chariot-text-primary font-bold">
                    Select All
                  </span>
                </div>
              </div>

              {/* Individual User Options */}
              {isLoading ? (
                <div className="text-chariot-text-secondary px-3 py-8 text-center text-sm">
                  Loading users...
                </div>
              ) : allUserEmails.length === 0 ? (
                <div className="text-chariot-text-secondary px-3 py-8 text-center text-sm">
                  No users available
                </div>
              ) : (
                allUserEmails.map(email => {
                  const usernameObj = usernames.find(u => u.email === email);
                  const displayName = usernameObj?.displayName || email;
                  const isSelected = usernameObj?.isSelected || false;
                  const sanitizedName = DOMPurify.sanitize(displayName, {
                    ALLOWED_TAGS: [],
                  });

                  return (
                    <ListboxOption
                      key={email}
                      value={email}
                      as={Fragment}
                      disabled={false}
                    >
                      {() => (
                        <div
                          className="hover:bg-chariot-background-secondary flex cursor-pointer items-center gap-3 px-3 py-2"
                          onClick={() => {
                            handleUserToggle(email);
                          }}
                        >
                          <div className="flex size-6 shrink-0 items-center justify-center">
                            <Checkbox isChecked={isSelected} />
                          </div>
                          <span className="text-chariot-text-primary min-w-0 flex-1 truncate font-medium">
                            {sanitizedName}
                          </span>
                          {isSelected && (
                            <CheckIcon className="text-chariot-brand-primary size-4 shrink-0" />
                          )}
                        </div>
                      )}
                    </ListboxOption>
                  );
                })
              )}
            </ListboxOptions>
          </Transition>
        </div>
      )}
    </Listbox>
  );
};
